import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { cn } from "../../lib/utils";

const toKey = (value) => String(value ?? "");

export default function MultiSelectField({
  label,
  required = false,
  options = [],
  selectedValues = [],
  onChange,
  single = false,
  placeholder = "Selecione...",
  searchPlaceholder = "Buscar...",
  emptyText = "Nenhuma opcao encontrada",
  disabled = false,
  className = "",
}) {
  const wrapperRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const normalizedOptions = useMemo(() => {
    const unique = new Map();
    options.forEach((option) => {
      if (option == null || option.value == null) return;
      const key = toKey(option.value);
      if (!unique.has(key)) {
        unique.set(key, {
          key,
          value: option.value,
          label: option.label ?? key,
          description: option.description ?? "",
        });
      }
    });
    return Array.from(unique.values());
  }, [options]);

  const optionMap = useMemo(() => {
    const map = new Map();
    normalizedOptions.forEach((option) => {
      map.set(option.key, option);
    });
    return map;
  }, [normalizedOptions]);

  const selectedKeys = useMemo(() => {
    const keys = [];
    selectedValues.forEach((value) => {
      const key = toKey(value);
      if (key && optionMap.has(key) && !keys.includes(key)) {
        keys.push(key);
      }
    });
    return keys;
  }, [selectedValues, optionMap]);

  const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const selectedOptions = useMemo(
    () => selectedKeys.map((key) => optionMap.get(key)).filter(Boolean),
    [selectedKeys, optionMap]
  );

  const filteredOptions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return normalizedOptions;
    return normalizedOptions.filter((option) => {
      return option.label.toLowerCase().includes(query) || option.description.toLowerCase().includes(query);
    });
  }, [normalizedOptions, searchTerm]);

  const selectedFilteredOptions = useMemo(
    () => filteredOptions.filter((option) => selectedKeySet.has(option.key)),
    [filteredOptions, selectedKeySet]
  );

  const availableFilteredOptions = useMemo(
    () => filteredOptions.filter((option) => !selectedKeySet.has(option.key)),
    [filteredOptions, selectedKeySet]
  );

  const emitKeys = (keys) => {
    const normalizedKeys = Array.from(new Set(keys.filter((key) => optionMap.has(key))));
    const values = normalizedKeys.map((key) => optionMap.get(key).value);
    onChange?.(values);
  };

  const toggleKey = (key) => {
    if (selectedKeySet.has(key)) {
      emitKeys(selectedKeys.filter((current) => current !== key));
      return;
    }
    if (single) {
      emitKeys([key]);
      setOpen(false);
      return;
    }
    emitKeys([...selectedKeys, key]);
  };

  const removeKey = (key) => {
    emitKeys(selectedKeys.filter((current) => current !== key));
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={cn("relative space-y-1.5 overflow-visible", open && "z-[90]", className)}
    >
      {label && (
        <label className="text-sm font-medium text-foreground">
          {label}
          {required && <span className="ml-1 text-destructive">*</span>}
        </label>
      )}

      <button
        type="button"
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        className={cn(
          "flex min-h-10 w-full items-center justify-between gap-2 rounded-lg border border-input bg-background px-3 py-2 text-left text-sm transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          open && "ring-2 ring-ring"
        )}
      >
        {selectedOptions.length === 0 ? (
          <span className="text-muted-foreground">{placeholder}</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {selectedOptions.slice(0, single ? 1 : 3).map((option) => (
              <span
                key={option.key}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium"
              >
                {option.label}
                <span
                  role="button"
                  tabIndex={-1}
                  className="rounded-sm p-0.5 text-muted-foreground transition hover:text-foreground"
                  onClick={(event) => {
                    event.stopPropagation();
                    removeKey(option.key);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      event.stopPropagation();
                      removeKey(option.key);
                    }
                  }}
                >
                  <X className="h-3 w-3" />
                </span>
              </span>
            ))}
            {!single && selectedOptions.length > 3 && (
              <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium">
                +{selectedOptions.length - 3}
              </span>
            )}
          </div>
        )}
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="absolute z-[95] mt-1 w-full rounded-xl border border-border bg-card p-2 shadow-lg">
          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={searchPlaceholder}
              className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          {!single && filteredOptions.length > 0 && (
            <div className="mb-3 px-1">
              <button
                type="button"
                onClick={() => {
                  const allSelected = filteredOptions.every((o) => selectedKeySet.has(o.key));
                  if (allSelected) {
                    const remaining = selectedKeys.filter((k) => !filteredOptions.some((o) => o.key === k));
                    emitKeys(remaining);
                  } else {
                    const newKeys = Array.from(new Set([...selectedKeys, ...filteredOptions.map((o) => o.key)]));
                    emitKeys(newKeys);
                  }
                }}
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                <div className={cn(
                  "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                  filteredOptions.every((o) => selectedKeySet.has(o.key))
                    ? "border-foreground bg-foreground"
                    : "border-border bg-background"
                )}>
                  {filteredOptions.every((o) => selectedKeySet.has(o.key)) && <Check className="h-3 w-3 text-background" />}
                </div>
                {filteredOptions.every((o) => selectedKeySet.has(o.key)) ? "Desmarcar todos" : "Selecionar todos"}
              </button>
            </div>
          )}

          {selectedOptions.length > 0 && (
            <div className="mb-2">
              <p className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Selecionados ({selectedOptions.length})
              </p>
              <div className="max-h-28 space-y-1 overflow-y-auto pr-1">
                {selectedFilteredOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => toggleKey(option.key)}
                    className="flex w-full items-center justify-between rounded-md border border-border/70 bg-muted/40 px-2 py-1.5 text-left text-sm transition hover:border-foreground/30"
                  >
                    <span className="truncate">{option.label}</span>
                    <Check className="h-4 w-4 text-foreground" />
                  </button>
                ))}
                {selectedFilteredOptions.length === 0 && (
                  <p className="px-1 text-xs text-muted-foreground">Nenhum selecionado para esta busca</p>
                )}
              </div>
            </div>
          )}

          <div>
            <p className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Disponiveis ({availableFilteredOptions.length})
            </p>
            <div className="max-h-36 space-y-1 overflow-y-auto pr-1">
              {availableFilteredOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => toggleKey(option.key)}
                  className="flex w-full items-center justify-between rounded-md border border-border/70 bg-background px-2 py-1.5 text-left text-sm transition hover:border-foreground/30"
                >
                  <span className="truncate">{option.label}</span>
                  <span className="h-4 w-4 rounded border border-border" />
                </button>
              ))}
              {availableFilteredOptions.length === 0 && (
                <p className="px-1 text-xs text-muted-foreground">{emptyText}</p>
              )}
            </div>
          </div>

          <div className="mt-2 flex items-center justify-end gap-2 border-t border-border/70 pt-2">
            {selectedOptions.length > 0 && (
              <button
                type="button"
                className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition hover:text-foreground"
                onClick={() => emitKeys([])}
              >
                Limpar
              </button>
            )}
            <button
              type="button"
              className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition hover:text-foreground"
              onClick={() => setOpen(false)}
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
